import { useEffect, useMemo, useState } from 'react';
import { type Member, type ProductRoles, type Role, Stewardship } from '@accounts/domain';
import { Refusal } from '@accounts/transport';
import { usePresence } from '@accounts/session';
import { Button, Callout, ChoiceCards, Dialog, EmptyState } from '@beyond-js/ui/react';
import { useUnread } from '@accounts/ui';
import type { Products } from './products';

const roles: Role[] = ['owner', 'admin', 'developer', 'viewer'];

/**
 * Changing a member's role in a dialog that says what each role means and what the change does
 * (ACC-05, ARR-10, ARR-12): every role with its description and, under it, what each product says the
 * role may do there; a warning before lowering your own role or making someone an owner; and, for the
 * last owner, no lower role offered, with the reason beside it. An administrator is not offered Owner.
 *
 * What each product says is read when the dialog opens, and named by the page's product list, which joins
 * the page's notice while the dialog is open (D163). When either is not answered, the products' place says
 * "Not available right now" under "In each product" rather than nothing, or a product's identifier.
 */
export function RoleDialog({
	organization,
	name,
	member,
	suggested,
	role,
	owners,
	products,
	onClose,
	onChoose
}: {
	organization: string;
	name: string;
	member?: Member;
	suggested?: Role;
	role?: Role;
	owners: number;
	products: Products;
	onClose: () => void;
	onChoose: (member: Member, role: Role) => Promise<void>;
}) {
	const { text, client, identity, language } = usePresence();
	const stewardship = useMemo(() => new Stewardship(client), [client]);
	const [chosen, setChosen] = useState<Role>();
	const [summaries, setSummaries] = useState<ProductRoles[]>([]);
	// Beyond Accounts did not answer what each product says: the dialog's own read, said in its place
	const [unsaid, setUnsaid] = useState(false);
	const unnamed = useUnread({ key: 'products', title: text('outage.products'), unread: products.unread, run: products.run }, Boolean(member)) && !products.list;
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		setChosen(suggested ?? member?.role);
		if (!member) return;
		setUnsaid(false);
		stewardship.roles(organization).then(setSummaries, (error: unknown) => {
			setSummaries([]);
			setUnsaid(Refusal.is(error) && error.unreachable);
		});
	}, [member, suggested, organization, stewardship]);

	if (!member) return null;
	const person = member.member ?? text('members.unnamed');
	const self = member.account === identity?.account.id;
	const last = member.role === 'owner' && owners <= 1;
	const offered = roles.filter(value => value !== 'owner' || role === 'owner');
	const named = (id: string) => products.list?.find(product => product.id === id)?.name ?? id;
	const away = unsaid || unnamed;

	async function submit() {
		if (!member || !chosen || chosen === member.role) return onClose();
		setBusy(true);
		try {
			await onChoose(member, chosen);
		} finally {
			setBusy(false);
		}
	}

	return (
		<Dialog
			open
			title={text('role.dialog.title', { name: person })}
			description={text('role.dialog.lead', { name: person, organization: name })}
			busy={busy}
			labels={Dialog.labels[language]}
			onClose={onClose}
			actions={
				<>
					<Button variant="quiet" onClick={onClose} disabled={busy}>
						{text('action.cancel')}
					</Button>
					<Button variant="primary" busy={busy} disabled={!chosen || chosen === member.role} onClick={() => void submit()}>
						{text('role.dialog.submit')}
					</Button>
				</>
			}
		>
			{/* The roles as cards to compare (DQ-15): each with its description and, as its facts, what each
			    product says the role may do there; a lower role the last owner cannot take stays visible
			    with its reason. The dialog's own button applies the choice, since a role change is confirmed */}
			<ChoiceCards
				label={text('members.role')}
				name="role"
				value={chosen ?? null}
				onChange={value => setChosen(value as Role)}
				options={offered.map(value => ({
					value,
					title: text(`role.${value}`),
					detail: text(`role.help.${value}.long`),
					facts: (away ? [] : summaries).filter(entry => entry.state === 'answered' && entry.roles[value]?.length).map(entry => `${named(entry.product)}: ${entry.roles[value].join(' · ')}`),
					disabled: last && value !== 'owner',
					reason: last && value !== 'owner' ? text('role.dialog.last') : null
				}))}
			/>
			{last ? <p className="mt-3 text-small text-ink-muted">{text('role.dialog.last')}</p> : null}
			{away ? (
				<div role="group" aria-label={text('role.dialog.products')} className="mt-3">
					<p data-bui-label className="text-small text-ink-muted">
						{text('role.dialog.products')}
					</p>
					<EmptyState kind="outage" frame={false} labels={EmptyState.labels[language]} />
				</div>
			) : null}
			{(away ? [] : summaries)
				.filter(entry => entry.state === 'unavailable')
				.map(entry => (
					<p key={entry.product} className="mt-2 text-small text-ink-muted">
						{text('role.dialog.unpublished', { product: named(entry.product) })}
					</p>
				))}
			{self && chosen && chosen !== member.role && !last ? <Callout tone="warning" title={text('role.dialog.self')} /> : null}
			{!self && chosen === 'owner' && member.role !== 'owner' ? <Callout tone="warning" title={text('role.dialog.owner', { name: person })} /> : null}
		</Dialog>
	);
}
